// PATH: src/pages/auth/auth/ChangePasswordRequired.tsx
//
// Écran de changement de mot de passe OBLIGATOIRE.
//
// Les comptes élèves créés à l'import d'une école partagent tous le même mot de
// passe provisoire, connu de toute l'école. Il ne doit servir qu'une fois : tant
// que l'élève n'en a pas choisi un à lui, `ProtectedRoute` le ramène ici quelle
// que soit la route demandée.
//
// La session EST ouverte à ce stade — refuser la connexion empêcherait
// justement de changer le mot de passe. C'est donc le routage qui contient
// l'utilisateur, pas l'authentification.
import React, { useMemo, useState } from 'react';
import { Form, Input, Button, Card, Typography, Layout, Alert } from 'antd';
import { LockOutlined, SafetyOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { App as AntdApp } from 'antd';
import { useAuthApi } from '../../../api/auth/auth';
import { useTilmiAuth } from '../../../hooks/useTilmiAuth';
import bg from '../../../assets/images/background/background_login.jpg';
import { appVersion } from '../../../config/config';
import LanguageSwitcher from '../../../components/auth/LanguageSwitcher';

const { Title, Text } = Typography;
const { Content, Footer } = Layout;

interface ChangePasswordFormValues {
    password: string;
    confirmPassword: string;
}

// Aligné sur la règle de tc_identity (passwordService.CreatePassword) : moins de
// 6 caractères est refusé côté serveur. Le dupliquer ici évite un aller-retour
// pour une erreur évidente, sans devenir la seule barrière.
const MIN_PASSWORD_LENGTH = 6;

const ChangePasswordRequired: React.FC = () => {
    const { t } = useTranslation();
    const { message } = AntdApp.useApp();
    const { replaceOwnPassword } = useAuthApi();
    const { refreshMe, logout, getUserAppId } = useTilmiAuth();
    const [form] = Form.useForm<ChangePasswordFormValues>();
    const [loading, setLoading] = useState(false);

    const palette = useMemo(
        () => ({
            primary: '#2D9CC9',
            secondary: '#8BBE1E',
            background: '#031323',
            surface: '#f5f9ff',
            outline: 'rgba(41, 146, 191, 0.22)',
            textPrimary: '#0c1324',
            textMuted: 'rgba(14, 21, 34, 0.62)',
        }),
        []
    );

    // getUserAppId resout deja user_id puis user.user.id, et retourne 0 quand
    // aucun n'est disponible — d'ou le test de verite plus bas plutot qu'un
    // test de nullite, que 0 ne declencherait pas.
    const userappId = getUserAppId();

    const onFinish = async (values: ChangePasswordFormValues) => {
        if (!userappId) {
            // Sans identifiant de compte, l'API refuserait de toute façon en 403.
            message.error(t('auth.changePasswordRequired.sessionError'));
            return;
        }
        setLoading(true);
        try {
            await replaceOwnPassword(Number(userappId), values.password);
            // Le drapeau retombe côté serveur au changement : recharger la
            // session est ce qui libère la navigation. Sans ce refresh,
            // l'utilisateur resterait bloqué sur cet écran malgré le succès.
            await refreshMe();
            message.success(t('auth.changePasswordRequired.success'));
        } catch (e: any) {
            message.error(
                e?.response?.data?.message ??
                    e?.response?.data?.error ??
                    t('auth.changePasswordRequired.error')
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <Layout
            style={{
                minHeight: '100vh',
                position: 'relative',
                overflow: 'hidden',
                background: palette.background,
            }}
        >
            <div style={{ position: 'absolute', top: 24, right: 24, zIndex: 1000 }}>
                <LanguageSwitcher variant="dark" />
            </div>
            <div
                style={{
                    position: 'absolute',
                    inset: 0,
                    backgroundImage: `linear-gradient(135deg, rgba(41,146,191,0.24), rgba(8,44,68,0.55)), url("${bg}")`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    filter: 'brightness(0.92)',
                }}
            />
            <Content
                style={{
                    position: 'relative',
                    zIndex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '48px 16px',
                }}
            >
                <Card
                    style={{
                        width: '100%',
                        maxWidth: 460,
                        borderRadius: 18,
                        border: `1px solid ${palette.outline}`,
                        background: palette.surface,
                        boxShadow: '0 24px 60px rgba(3, 19, 35, 0.35)',
                    }}
                    styles={{ body: { padding: 32 } }}
                >
                    <div style={{ textAlign: 'center', marginBottom: 24 }}>
                        <SafetyOutlined style={{ fontSize: 40, color: palette.primary }} />
                        <Title level={3} style={{ marginTop: 12, marginBottom: 4, color: palette.textPrimary }}>
                            {t('auth.changePasswordRequired.title')}
                        </Title>
                        <Text style={{ color: palette.textMuted }}>
                            {t('auth.changePasswordRequired.subtitle')}
                        </Text>
                    </div>

                    <Alert
                        type="warning"
                        showIcon
                        style={{ marginBottom: 24 }}
                        message={t('auth.changePasswordRequired.warningTitle')}
                        description={t('auth.changePasswordRequired.warningBody')}
                    />

                    <Form form={form} layout="vertical" onFinish={onFinish} requiredMark={false}>
                        <Form.Item
                            name="password"
                            label={t('auth.changePasswordRequired.newPassword')}
                            rules={[
                                { required: true, message: t('auth.changePasswordRequired.passwordRequired') },
                                {
                                    min: MIN_PASSWORD_LENGTH,
                                    message: t('auth.changePasswordRequired.passwordTooShort', {
                                        count: MIN_PASSWORD_LENGTH,
                                    }),
                                },
                            ]}
                            hasFeedback
                        >
                            <Input.Password
                                size="large"
                                prefix={<LockOutlined style={{ color: palette.textMuted }} />}
                                placeholder={t('auth.changePasswordRequired.newPasswordPlaceholder')}
                                autoComplete="new-password"
                            />
                        </Form.Item>

                        <Form.Item
                            name="confirmPassword"
                            label={t('auth.changePasswordRequired.confirmPassword')}
                            dependencies={['password']}
                            hasFeedback
                            rules={[
                                { required: true, message: t('auth.changePasswordRequired.confirmRequired') },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (!value || getFieldValue('password') === value) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(
                                            new Error(t('auth.changePasswordRequired.mismatch'))
                                        );
                                    },
                                }),
                            ]}
                        >
                            <Input.Password
                                size="large"
                                prefix={<LockOutlined style={{ color: palette.textMuted }} />}
                                placeholder={t('auth.changePasswordRequired.confirmPasswordPlaceholder')}
                                autoComplete="new-password"
                            />
                        </Form.Item>

                        <Button
                            type="primary"
                            htmlType="submit"
                            size="large"
                            block
                            loading={loading}
                            style={{ background: palette.primary, borderColor: palette.primary, marginTop: 8 }}
                        >
                            {t('auth.changePasswordRequired.submit')}
                        </Button>
                    </Form>

                    {/* Seule sortie possible sans changer le mot de passe : se
                        déconnecter. Un élève qui s'est trompé de compte ne doit
                        pas rester prisonnier de cet écran. */}
                    <Button type="link" block onClick={() => void logout()} style={{ marginTop: 12 }}>
                        {t('auth.changePasswordRequired.logout')}
                    </Button>
                </Card>
            </Content>
            <Footer
                style={{
                    position: 'relative',
                    zIndex: 1,
                    textAlign: 'center',
                    background: 'transparent',
                    color: 'rgba(255,255,255,0.65)',
                }}
            >
                Tilmicode {appVersion}
            </Footer>
        </Layout>
    );
};

export default ChangePasswordRequired;
